import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../ui/command';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { CircleDot, Check, ChevronsUpDown, FileText } from 'lucide-react';
import SendEmailModal from '../shared/SendEmailModal';

const STATUS_OPTIONS = [
  { value: 'müsait', label: 'Müsait', color: '#22c55e' },
  { value: 'rezerve', label: 'Rezerve', color: '#f59e0b' },
  { value: 'satıldı', label: 'Satıldı', color: '#ef4444' },
];

const STATUS_COLORS = {
  'müsait': '#22c55e',
  'rezerve': '#f59e0b',
  'satıldı': '#ef4444',
};

const CURRENCY_OPTIONS = [
  { value: 'TRY', label: '₺ TRY', symbol: '₺' },
  { value: 'USD', label: '$ USD', symbol: '$' },
  { value: 'EUR', label: '€ EUR', symbol: '€' },
  { value: 'GBP', label: '£ GBP', symbol: '£' },
];

const EMPTY_FORM = {
  number: '', name: '', color: '#10b981', opacity: 1.0, show_area: true,
  width: 1, height: 1,
  status: 'müsait', price_per_sqm: '', price_currency: 'TRY',
  assigned_company_id: '', assigned_company_name: '',
  hide_status_for_customer: true, hide_name_for_customer: true,
  ceiling_height: '', max_stand_height: '',
};

export default function StandEditor({ 
  exhibition, 
  hall, 
  gridWidth, 
  gridHeight,
  zoom,
  onStandsChange
}) {
  const [stands, setStands] = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [selectedArea, setSelectedArea] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [standForm, setStandForm] = useState({ ...EMPTY_FORM, price_currency: 'TRY' });
  const [hoveredStand, setHoveredStand] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingStand, setEditingStand] = useState(null);
  const [draggingStand, setDraggingStand] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const baseCurrency = exhibition?.base_currency || 'TRY';
  const defaultPrice = exhibition?.default_price_per_sqm || '';
  const getEmptyForm = () => ({ ...EMPTY_FORM, price_currency: baseCurrency, price_per_sqm: defaultPrice });
  const [companySearchOpen, setCompanySearchOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [allExhibitionStands, setAllExhibitionStands] = useState([]);
  // Contract creation state
  const [showStatusWarning, setShowStatusWarning] = useState(false);
  const [warningStatusLabel, setWarningStatusLabel] = useState('');
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractTemplates, setContractTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [creatingContract, setCreatingContract] = useState(false);
  const [showMailModal, setShowMailModal] = useState(false);

  useEffect(() => {
    if (hall) {
      fetchStands();
      fetchCompanies();
      fetchAllExhibitionStands();
    }
  }, [hall]); // eslint-disable-line

  const fetchStands = async () => {
    try {
      const res = await axios.get(`${API}/stands?exhibition_id=${exhibition.id}&hall_id=${hall.id}`);
      setStands(res.data);
      if (onStandsChange) onStandsChange(res.data);
    } catch (error) {
      console.error('Stands yüklenemedi');
    }
  };

  const fetchAllExhibitionStands = async () => {
    try {
      const res = await axios.get(`${API}/stands?exhibition_id=${exhibition.id}`);
      setAllExhibitionStands(res.data);
    } catch {
      // silently fail
    }
  };

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/companies?filter=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data.filter(c => !c.is_blacklisted));
    } catch {
      // silently fail
    }
  };

  const fetchContractTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/contract-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContractTemplates(res.data);
    } catch {
      // silently fail
    }
  };

  const handleCreateContract = async () => {
    if (!selectedTemplateId) return toast.error('Lütfen bir şablon seçin');
    if (!editingStand?.assigned_company_id) return toast.error('Stand\'a atanmış firma yok');
    setCreatingContract(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/contracts`, {
        stand_id: editingStand.id,
        template_id: selectedTemplateId,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Sözleşme oluşturuldu! Sözleşmeler sayfasından görüntüleyebilirsiniz.');
      setShowContractModal(false);
      setSelectedTemplateId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sözleşme oluşturulamadı');
    } finally {
      setCreatingContract(false);
    }
  };

  const createStand = async () => {
    if (!selectedArea) return;

    const parseD = (v) => parseFloat(String(v).replace(',', '.')) || 0.5;
    const w = parseD(standForm.width) || selectedArea.width;
    const h = parseD(standForm.height) || selectedArea.height;

    // Check for overlap
    const overlapping = stands.find(s => !(
      selectedArea.x + w <= s.x ||
      selectedArea.x >= s.x + s.width ||
      selectedArea.y + h <= s.y ||
      selectedArea.y >= s.y + s.height
    ));

    if (overlapping) {
      toast.error(`Bu alan ${overlapping.stand_number || '?'} ile çakışıyor!`);
      return;
    }

    // Resolve company name
    let assignedCompanyName = standForm.assigned_company_name;
    if (standForm.assigned_company_id) {
      const found = companies.find(c => c.id === standForm.assigned_company_id);
      if (found) assignedCompanyName = found.name;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/stands`, {
        exhibition_id: exhibition.id,
        hall_id: hall.id,
        stand_number: standForm.number || '',
        stand_name: assignedCompanyName || standForm.name || '',
        show_area: standForm.show_area,
        x: selectedArea.x,
        y: selectedArea.y,
        width: w,
        height: h,
        path: selectedArea.path || null,
        shape_type: selectedArea.shape_type || null,
        is_column: selectedArea.is_column || false,
        color: standForm.color,
        opacity: standForm.opacity,
        status: standForm.status,
        price_per_sqm: standForm.price_per_sqm ? parseFloat(standForm.price_per_sqm) : null,
        price_currency: standForm.price_currency || baseCurrency,
        assigned_company_id: standForm.assigned_company_id || null,
        assigned_company_name: assignedCompanyName || null,
        hide_status_for_customer: standForm.hide_status_for_customer,
        hide_name_for_customer: standForm.hide_name_for_customer,
        ceiling_height: standForm.ceiling_height ? parseFloat(standForm.ceiling_height) : null,
        max_stand_height: standForm.max_stand_height ? parseFloat(standForm.max_stand_height) : null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stand oluşturuldu');
      setShowCreateDialog(false);
      setStandForm(getEmptyForm());
      setSelectedArea(null);
      fetchStands();
      fetchAllExhibitionStands();
    } catch (error) {
      toast.error('Stand oluşturulamadı');
    }
  };

  const updateStand = async () => {
    if (!editingStand) return;

    try {
      const token = localStorage.getItem('token');

      // Resolve company name if company selected
      let assignedCompanyName = standForm.assigned_company_name;
      if (standForm.assigned_company_id) {
        const found = companies.find(c => c.id === standForm.assigned_company_id);
        if (found) assignedCompanyName = found.name;
      }

      const updateData = {
        stand_number: standForm.number || '',
        stand_name: assignedCompanyName || standForm.name || '',
        show_area: standForm.show_area,
        width: parseFloat(String(standForm.width).replace(',', '.')) || 0.5,
        height: parseFloat(String(standForm.height).replace(',', '.')) || 0.5,
        color: standForm.color,
        opacity: standForm.opacity,
        status: standForm.status,
        price_per_sqm: standForm.price_per_sqm ? parseFloat(standForm.price_per_sqm) : null,
        price_currency: standForm.price_currency || baseCurrency,
        assigned_company_id: standForm.assigned_company_id || null,
        assigned_company_name: assignedCompanyName || null,
        hide_status_for_customer: standForm.hide_status_for_customer,
        hide_name_for_customer: standForm.hide_name_for_customer,
        ceiling_height: standForm.ceiling_height ? parseFloat(standForm.ceiling_height) : null,
        max_stand_height: standForm.max_stand_height ? parseFloat(standForm.max_stand_height) : null,
        ...(editingStand?.is_column && { shape_type: standForm.shape_type }),
      };

      await axios.patch(`${API}/stands/${editingStand.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stand güncellendi');
      setShowEditDialog(false);
      setEditingStand(null);
      setStandForm(getEmptyForm());
      fetchStands();
      fetchAllExhibitionStands();
    } catch (error) {
      console.error('Stand update error:', error.response?.data);
      toast.error('Stand güncellenemedi');
    }
  };

  const updateStandPosition = async (stand) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/stands/${stand.id}`, { x: stand.x, y: stand.y }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchStands();
    } catch (error) {
      console.error('Stand pozisyonu güncellenemedi');
    }
  };

  const deleteStand = async (standId) => {
    if (!window.confirm('Bu standı silmek istediğinize emin misiniz?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/stands/${standId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Stand silindi');
      setShowEditDialog(false);
      fetchStands();
    } catch (error) {
      toast.error('Stand silinemedi');
    }
  };

  const copyStand = async (stand) => {
    // Start near original stand position
    let newX = stand.x + 2;
    let newY = stand.y + 2;

    // Try to find a non-overlapping position nearby
    let attempts = 0;
    while (attempts < 10) {
      const overlapping = stands.find(s => !(
        newX + stand.width <= s.x ||
        newX >= s.x + s.width ||
        newY + stand.height <= s.y ||
        newY >= s.y + s.height
      ));
      if (!overlapping) break;
      newX += 2;
      newY += 2;
      attempts++;
    }

    newX = Math.min(gridWidth - stand.width, Math.max(0, newX));
    newY = Math.min(gridHeight - stand.height, Math.max(0, newY));

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/stands`, {
        exhibition_id: exhibition.id,
        hall_id: hall.id,
        stand_number: stand.stand_number + '-K',
        stand_name: stand.stand_name,
        show_area: stand.show_area,
        path: stand.path,
        x: newX,
        y: newY,
        width: stand.width,
        height: stand.height,
        color: stand.color,
        opacity: stand.opacity,
        status: stand.status,
        is_column: stand.is_column || false,
        shape_type: stand.shape_type || null,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchStands();
      // Return the new stand so caller can enter move mode
      return res.data;
    } catch (error) {
      toast.error('Stand kopyalanamadı');
      return null;
    }
  };

  const openEditDialog = (stand) => {
    const standStatus = stand.status || 'müsait';
    setEditingStand(stand);
    setHoveredStand(null);
    setStandForm({
      number: stand.stand_number || '',
      name: stand.stand_name || '',
      color: stand.is_column ? (stand.color || '#94a3b8') : (STATUS_COLORS[standStatus] || stand.color),
      opacity: stand.opacity,
      width: stand.width,
      height: stand.height,
      show_area: stand.show_area !== undefined ? stand.show_area : true,
      status: standStatus,
      price_per_sqm: stand.price_per_sqm || '',
      price_currency: stand.price_currency || baseCurrency,
      assigned_company_id: stand.assigned_company_id || '',
      assigned_company_name: stand.assigned_company_name || '',
      hide_status_for_customer: stand.hide_status_for_customer !== undefined ? stand.hide_status_for_customer : true,
      hide_name_for_customer: stand.hide_name_for_customer !== undefined ? stand.hide_name_for_customer : true,
      ceiling_height: stand.ceiling_height != null ? String(stand.ceiling_height) : '',
      max_stand_height: stand.max_stand_height != null ? String(stand.max_stand_height) : '',
      shape_type: stand.shape_type || 'square_column',
    });
    setShowEditDialog(true);
  };

  // Build a map of company_id -> count of assigned stands in this exhibition
  const getCompanyAssignmentMap = (excludeStandId = null) => {
    const map = {};
    allExhibitionStands.forEach(s => {
      if (s.assigned_company_id && s.id !== excludeStandId) {
        map[s.assigned_company_id] = (map[s.assigned_company_id] || 0) + 1;
      }
    });
    return map;
  };

  const getTextColor = (hexColor, opacity) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return (brightness > 128 && opacity > 0.5) ? '#000000' : '#ffffff';
  };

  // Reset form to defaults (with exhibition's default price and currency)
  const resetFormToDefaults = () => {
    setStandForm(getEmptyForm());
  };

  return {
    stands,
    setStands,
    selecting,
    setSelecting,
    selectionStart,
    setSelectionStart,
    selectionEnd,
    setSelectionEnd,
    selectedArea,
    setSelectedArea,
    showCreateDialog,
    setShowCreateDialog,
    standForm,
    setStandForm,
    hoveredStand,
    setHoveredStand,
    showEditDialog,
    setShowEditDialog,
    editingStand,
    draggingStand,
    setDraggingStand,
    dragOffset,
    setDragOffset,
    createStand,
    updateStand,
    updateStandPosition,
    deleteStand,
    copyStand,
    openEditDialog,
    openContractModal: (stand) => {
      setEditingStand(stand);
      fetchContractTemplates();
      setSelectedTemplateId('');
      setShowContractModal(true);
    },
    openMailModal: (stand) => {
      setEditingStand(stand);
      setShowMailModal(true);
    },
    getTextColor,
    fetchStands,
    resetFormToDefaults,
    CreateStandDialog: () => (
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Yeni Stand Oluştur</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2 pt-1 flex-wrap">
                <span className="text-xs text-gray-500">Alan:</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={standForm.width}
                    onChange={e => setStandForm({...standForm, width: e.target.value})}
                    className="w-16 h-7 text-sm px-2 py-0"
                    data-testid="create-stand-width"
                  />
                  <span className="text-xs text-gray-500">m</span>
                </div>
                <span className="text-gray-400 text-sm">×</span>
                <div className="flex items-center gap-1">
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={standForm.height}
                    onChange={e => setStandForm({...standForm, height: e.target.value})}
                    className="w-16 h-7 text-sm px-2 py-0"
                    data-testid="create-stand-height"
                  />
                  <span className="text-xs text-gray-500">m</span>
                </div>
                <span className="text-xs text-gray-400">
                  = {(parseFloat(standForm.width || 0) * parseFloat(standForm.height || 0)).toFixed(2)} m²
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Status */}
            <div className="space-y-2">
              <Label>Stand Durumu</Label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      if ((opt.value === 'rezerve' || opt.value === 'satıldı') && !standForm.assigned_company_id) {
                        setWarningStatusLabel(opt.value === 'rezerve' ? 'Rezerve' : 'Satış');
                        setShowStatusWarning(true);
                        return;
                      }
                      const patch = { status: opt.value, color: STATUS_COLORS[opt.value] };
                      if (opt.value === 'müsait') {
                        patch.assigned_company_id = '';
                        patch.assigned_company_name = '';
                        patch.name = '';
                      }
                      setStandForm({ ...standForm, ...patch });
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg border-2 text-xs font-medium transition-all ${
                      standForm.status === opt.value ? 'border-current text-white' : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    style={standForm.status === opt.value ? { backgroundColor: opt.color, borderColor: opt.color } : {}}
                    data-testid={`create-status-${opt.value}`}
                  >
                    <CircleDot className="w-3 h-3" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Company assignment - Searchable Combobox */}
            <div className="space-y-2">
              <Label>Firma Atama</Label>
              <Popover open={companySearchOpen} onOpenChange={setCompanySearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={companySearchOpen}
                    className="w-full justify-between font-normal"
                    data-testid="create-company-select"
                  >
                    {standForm.assigned_company_id
                      ? (companies.find(c => c.id === standForm.assigned_company_id)?.name || 'Firma seçin...')
                      : 'Firma seçin...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Firma ara..." />
                    <CommandList>
                      <CommandEmpty>Firma bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="firma-atanmadi"
                          onSelect={() => {
                            setStandForm({
                              ...standForm,
                              assigned_company_id: '',
                              assigned_company_name: '',
                            });
                            setCompanySearchOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!standForm.assigned_company_id ? 'opacity-100' : 'opacity-0'}`} />
                          — Firma Atanmadı —
                        </CommandItem>
                        {(() => {
                          const assignMap = getCompanyAssignmentMap(null);
                          return companies.map(c => {
                            const assignedCount = assignMap[c.id] || 0;
                            return (
                              <CommandItem
                                key={c.id}
                                value={c.name}
                                onSelect={() => {
                                  setStandForm({
                                    ...standForm,
                                    assigned_company_id: c.id,
                                    assigned_company_name: c.name,
                                    name: c.name,
                                    status: standForm.status === 'müsait' ? 'rezerve' : standForm.status,
                                    color: standForm.status === 'müsait' ? STATUS_COLORS['rezerve'] : standForm.color,
                                  });
                                  setCompanySearchOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${standForm.assigned_company_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span>{c.name}</span>
                                {assignedCount > 0 && (
                                  <span className="ml-auto text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                    {assignedCount} stand
                                  </span>
                                )}
                              </CommandItem>
                            );
                          });
                        })()}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>m² Birim Fiyatı</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={standForm.price_per_sqm}
                  onChange={(e) => setStandForm({ ...standForm, price_per_sqm: e.target.value })}
                  placeholder="Örn: 500"
                  min="0"
                  step="10"
                  className="flex-1"
                  data-testid="create-price-input"
                />
                <Select
                  value={standForm.price_currency}
                  onValueChange={(val) => setStandForm({ ...standForm, price_currency: val })}
                >
                  <SelectTrigger className="w-28" data-testid="create-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {standForm.price_per_sqm && (
                <p className="text-xs text-gray-500">
                  Toplam: {CURRENCY_OPTIONS.find(c => c.value === standForm.price_currency)?.symbol}{(parseFloat(standForm.price_per_sqm) * (parseFloat(standForm.width || 0) * parseFloat(standForm.height || 0))).toLocaleString('tr-TR')}
                </p>
              )}
            </div>

            <hr className="border-gray-200" />

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Stand No <span className="text-xs text-gray-400">(isteğe bağlı)</span></Label>
                <Input
                  value={standForm.number}
                  onChange={(e) => setStandForm({...standForm, number: e.target.value})}
                  placeholder="A125"
                  data-testid="stand-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Stand Adı <span className="text-xs text-gray-400">(isteğe bağlı)</span></Label>
                <Input
                  value={standForm.name}
                  onChange={(e) => setStandForm({...standForm, name: e.target.value})}
                  placeholder="Firma adı"
                  data-testid="stand-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Renk</Label>
              <div className="flex gap-2">
                <Input type="color" value={standForm.color} onChange={(e) => setStandForm({...standForm, color: e.target.value})} className="w-16 h-10" />
                <Input value={standForm.color} onChange={(e) => setStandForm({...standForm, color: e.target.value})} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Şeffaflık: {(standForm.opacity * 100).toFixed(0)}%</Label>
              <input type="range" min="0" max="1" step="0.1" value={standForm.opacity}
                onChange={(e) => setStandForm({...standForm, opacity: parseFloat(e.target.value)})} className="w-full" />
            </div>

            <div className="flex items-center space-x-2">
              <input type="checkbox" id="create-show-area" checked={standForm.show_area}
                onChange={(e) => setStandForm({...standForm, show_area: e.target.checked})}
                className="w-4 h-4" data-testid="show-area-checkbox" />
              <Label htmlFor="create-show-area" className="cursor-pointer">m² Göster</Label>
            </div>

            <hr className="border-gray-100" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Yükseklik Bilgileri</p>
            <div className="grid grid-cols-2 gap-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="space-y-1.5">
                <Label className="text-xs">Tavan Yüksekliği (m)</Label>
                <Input
                  type="number" step="0.1" min="1"
                  value={standForm.ceiling_height}
                  onChange={e => setStandForm({ ...standForm, ceiling_height: e.target.value })}
                  placeholder="ör: 6.0"
                  data-testid="stand-ceiling-height-input"
                />
                {selectedArea?.ceiling_height && standForm.ceiling_height && (
                  <p className="text-xs text-amber-600">Bölgeden otomatik atandı</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Maks. Stand Yüksekliği (m)</Label>
                <Input
                  type="number" step="0.1" min="0.5"
                  value={standForm.max_stand_height}
                  onChange={e => setStandForm({ ...standForm, max_stand_height: e.target.value })}
                  placeholder="ör: 4.0"
                  data-testid="stand-max-height-input"
                />
              </div>
            </div>

            <hr className="border-gray-100" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Müşteri Görünümü</p>
            <div className="space-y-2 bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="create-hide-status" checked={standForm.hide_status_for_customer}
                  onChange={(e) => setStandForm({...standForm, hide_status_for_customer: e.target.checked})}
                  className="w-4 h-4" data-testid="hide-status-checkbox" />
                <Label htmlFor="create-hide-status" className="cursor-pointer text-sm">
                  Müşteri satış statüsünü görmesin
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="create-hide-name" checked={standForm.hide_name_for_customer}
                  onChange={(e) => setStandForm({...standForm, hide_name_for_customer: e.target.checked})}
                  className="w-4 h-4" data-testid="hide-name-checkbox" />
                <Label htmlFor="create-hide-name" className="cursor-pointer text-sm">
                  Müşteri firma ismini görmesin
                </Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => { setShowCreateDialog(false); }} variant="outline" className="flex-1"
                data-testid="cancel-create-stand-btn">Vazgeç</Button>
              <Button onClick={createStand} className="flex-1" data-testid="create-stand-btn">
                Stand Oluştur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    ),
    EditStandDialog: () => (
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStand?.is_column ? `Kolon Düzenle - ${editingStand?.stand_number}` : `Stand Düzenle - ${editingStand?.stand_number}`}
            </DialogTitle>
          </DialogHeader>

          {/* ── Simplified column editor ─────────────────────────────── */}
          {editingStand?.is_column ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Etiket</Label>
                <Input
                  value={standForm.number}
                  onChange={e => setStandForm({...standForm, number: e.target.value})}
                  placeholder="K-01"
                  data-testid="edit-column-number"
                />
              </div>

              <div className="space-y-2">
                <Label>Şekil</Label>
                <div className="flex gap-2">
                  {[{type:'square_column',label:'Kare'},{type:'rect_column',label:'Dikdörtgen'},{type:'circle_column',label:'Silindir'}].map(ct => (
                    <button key={ct.type} type="button"
                      onClick={() => setStandForm({
                        ...standForm,
                        shape_type: ct.type,
                        // Kare/Silindir seçilince Boy = En yap
                        ...(ct.type !== 'rect_column' && { height: standForm.width }),
                      })}
                      className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${standForm.shape_type === ct.type ? 'bg-gray-800 text-white border-gray-800' : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}
                      data-testid={`column-shape-${ct.type}`}>
                      {ct.label}
                    </button>
                  ))}
                </div>
              </div>

              {standForm.shape_type === 'circle_column' ? (
                <div className="space-y-2">
                  <Label>Çap (m)</Label>
                  <Input type="text" inputMode="decimal"
                    value={standForm.width}
                    onChange={e => setStandForm({...standForm, width: e.target.value, height: e.target.value})}
                    data-testid="edit-column-width" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>En (m)</Label>
                    <Input type="text" inputMode="decimal"
                      value={standForm.width}
                      onChange={e => setStandForm({
                        ...standForm,
                        width: e.target.value,
                        ...(standForm.shape_type !== 'rect_column' && { height: e.target.value }),
                      })}
                      data-testid="edit-column-width" />
                  </div>
                  <div className={`space-y-2 ${standForm.shape_type !== 'rect_column' ? 'opacity-40 pointer-events-none' : ''}`}>
                    <Label>Boy (m)</Label>
                    <Input type="text" inputMode="decimal"
                      value={standForm.height}
                      onChange={e => setStandForm({...standForm, height: e.target.value})}
                      data-testid="edit-column-height" />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Renk</Label>
                <div className="flex gap-2">
                  <Input type="color" value={standForm.color}
                    onChange={e => setStandForm({...standForm, color: e.target.value})}
                    className="w-20 h-10" />
                  <Input value={standForm.color}
                    onChange={e => setStandForm({...standForm, color: e.target.value})} />
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <Button variant="destructive" size="sm"
                  onClick={() => { deleteStand(editingStand.id); setShowEditDialog(false); }}
                  data-testid="delete-column-btn">
                  Sil
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setShowEditDialog(false)}>İptal</Button>
                  <Button onClick={updateStand} data-testid="save-column-btn">Kaydet</Button>
                </div>
              </div>
            </div>
          ) : (
          <div className="space-y-4">
            {/* Status */}
            <div className="space-y-2">
              <Label>Stand Durumu</Label>
              <div className="flex gap-2">
                {STATUS_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      if ((opt.value === 'rezerve' || opt.value === 'satıldı') && !standForm.assigned_company_id) {
                        setWarningStatusLabel(opt.value === 'rezerve' ? 'Rezerve' : 'Satış');
                        setShowStatusWarning(true);
                        return;
                      }
                      const patch = { status: opt.value, color: STATUS_COLORS[opt.value] };
                      if (opt.value === 'müsait') {
                        patch.assigned_company_id = '';
                        patch.assigned_company_name = '';
                        patch.name = '';
                      }
                      setStandForm({ ...standForm, ...patch });
                    }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                      standForm.status === opt.value
                        ? 'border-current text-white'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                    style={standForm.status === opt.value ? { backgroundColor: opt.color, borderColor: opt.color } : {}}
                    data-testid={`status-${opt.value}`}
                  >
                    <CircleDot className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Company assignment - Searchable Combobox */}
            <div className="space-y-2">
              <Label>Firma Atama</Label>
              <Popover open={companySearchOpen} onOpenChange={setCompanySearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={companySearchOpen}
                    className="w-full justify-between font-normal"
                    data-testid="company-select"
                  >
                    {standForm.assigned_company_id
                      ? (companies.find(c => c.id === standForm.assigned_company_id)?.name || 'Firma seçin...')
                      : 'Firma seçin...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Firma ara..." />
                    <CommandList>
                      <CommandEmpty>Firma bulunamadı.</CommandEmpty>
                      <CommandGroup>
                        <CommandItem
                          value="firma-atanmadi"
                          onSelect={() => {
                            setStandForm({
                              ...standForm,
                              assigned_company_id: '',
                              assigned_company_name: '',
                            });
                            setCompanySearchOpen(false);
                          }}
                        >
                          <Check className={`mr-2 h-4 w-4 ${!standForm.assigned_company_id ? 'opacity-100' : 'opacity-0'}`} />
                          — Firma Atanmadı —
                        </CommandItem>
                        {(() => {
                          const assignMap = getCompanyAssignmentMap(editingStand?.id);
                          return companies.map(c => {
                            const assignedCount = assignMap[c.id] || 0;
                            return (
                              <CommandItem
                                key={c.id}
                                value={c.name}
                                onSelect={() => {
                                  setStandForm({
                                    ...standForm,
                                    assigned_company_id: c.id,
                                    assigned_company_name: c.name,
                                    status: standForm.status === 'müsait' ? 'rezerve' : standForm.status,
                                    color: standForm.status === 'müsait' ? STATUS_COLORS['rezerve'] : standForm.color,
                                  });
                                  setCompanySearchOpen(false);
                                }}
                              >
                                <Check className={`mr-2 h-4 w-4 ${standForm.assigned_company_id === c.id ? 'opacity-100' : 'opacity-0'}`} />
                                <span>{c.name}</span>
                                {assignedCount > 0 && (
                                  <span className="ml-auto text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                                    {assignedCount} stand
                                  </span>
                                )}
                              </CommandItem>
                            );
                          });
                        })()}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {companies.length === 0 && (
                <p className="text-xs text-gray-400">
                  Önce Firmalar menüsünden firma ekleyin
                </p>
              )}
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label>m² Birim Fiyatı</Label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={standForm.price_per_sqm}
                  onChange={(e) => setStandForm({ ...standForm, price_per_sqm: e.target.value })}
                  placeholder="Örn: 500"
                  min="0"
                  step="10"
                  className="flex-1"
                  data-testid="price-per-sqm-input"
                />
                <Select
                  value={standForm.price_currency}
                  onValueChange={(val) => setStandForm({ ...standForm, price_currency: val })}
                >
                  <SelectTrigger className="w-28" data-testid="edit-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCY_OPTIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {standForm.price_per_sqm && (
                <p className="text-xs text-gray-500 mt-1">
                  Toplam: {CURRENCY_OPTIONS.find(c => c.value === standForm.price_currency)?.symbol}{(parseFloat(standForm.price_per_sqm) * (standForm.width * standForm.height)).toLocaleString('tr-TR')}
                  {' '}({standForm.width * standForm.height} m² × {standForm.price_per_sqm} {standForm.price_currency}/m²)
                </p>
              )}
            </div>

            <hr className="border-gray-200" />

            <div className="space-y-2">
              <Label>Stand Numarası (Küçük - Sağ Üstte)</Label>
              <Input
                value={standForm.number}
                onChange={(e) => setStandForm({...standForm, number: e.target.value})}
                data-testid="edit-stand-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Stand Adı / Firma Adı (Büyük - Ortada)</Label>
              <Input
                value={standForm.name}
                onChange={(e) => setStandForm({...standForm, name: e.target.value})}
                placeholder="Örn: XYZ Şirketi"
                data-testid="edit-stand-name-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-show-area"
                checked={standForm.show_area}
                onChange={(e) => setStandForm({...standForm, show_area: e.target.checked})}
                className="w-4 h-4"
                data-testid="edit-show-area-checkbox"
              />
              <Label htmlFor="edit-show-area" className="cursor-pointer">m² Göster</Label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>En (m)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={standForm.width}
                  onChange={(e) => setStandForm({...standForm, width: e.target.value})}
                  data-testid="edit-stand-width"
                />
              </div>
              <div className="space-y-2">
                <Label>Boy (m)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={standForm.height}
                  onChange={(e) => setStandForm({...standForm, height: e.target.value})}
                  data-testid="edit-stand-height"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Renk</Label>
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={standForm.color}
                  onChange={(e) => setStandForm({...standForm, color: e.target.value})}
                  className="w-20 h-10"
                />
                <Input
                  value={standForm.color}
                  onChange={(e) => setStandForm({...standForm, color: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Şeffaflık: {(standForm.opacity * 100).toFixed(0)}%</Label>
              <input
                type="range" min="0" max="1" step="0.1"
                value={standForm.opacity}
                onChange={(e) => setStandForm({...standForm, opacity: parseFloat(e.target.value)})}
                className="w-full"
              />
            </div>

            <hr className="border-gray-100" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Yükseklik Bilgileri (Edit)</p>
            <div className="grid grid-cols-2 gap-3 bg-amber-50 rounded-lg p-3 border border-amber-100">
              <div className="space-y-1.5">
                <Label className="text-xs">Tavan Yüksekliği (m)</Label>
                <Input
                  type="number" step="0.1" min="1"
                  value={standForm.ceiling_height}
                  onChange={e => setStandForm({ ...standForm, ceiling_height: e.target.value })}
                  placeholder="ör: 6.0"
                  data-testid="edit-ceiling-height-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Maks. Stand Yüksekliği (m)</Label>
                <Input
                  type="number" step="0.1" min="0.5"
                  value={standForm.max_stand_height}
                  onChange={e => setStandForm({ ...standForm, max_stand_height: e.target.value })}
                  placeholder="ör: 4.0"
                  data-testid="edit-max-height-input"
                />
              </div>
            </div>

            <hr className="border-gray-100" />
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Müşteri Görünümü</p>
            <div className="space-y-2 bg-gray-50 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="edit-hide-status" checked={standForm.hide_status_for_customer}
                  onChange={(e) => setStandForm({...standForm, hide_status_for_customer: e.target.checked})}
                  className="w-4 h-4" data-testid="edit-hide-status-checkbox" />
                <Label htmlFor="edit-hide-status" className="cursor-pointer text-sm">Müşteri satış statüsünü görmesin</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input type="checkbox" id="edit-hide-name" checked={standForm.hide_name_for_customer}
                  onChange={(e) => setStandForm({...standForm, hide_name_for_customer: e.target.checked})}
                  className="w-4 h-4" data-testid="edit-hide-name-checkbox" />
                <Label htmlFor="edit-hide-name" className="cursor-pointer text-sm">Müşteri firma ismini görmesin</Label>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => setShowEditDialog(false)}
                variant="outline" className="flex-1"
                data-testid="cancel-edit-btn"
              >
                Vazgeç
              </Button>
              <Button onClick={updateStand} className="flex-1" data-testid="update-stand-btn">
                Kaydet
              </Button>
            </div>

            {/* Contract button - show immediately when form status is rezerve or satıldı */}
            {(standForm.status === 'rezerve' || standForm.status === 'satıldı') && (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 border-teal-300 text-teal-700 hover:bg-teal-50"
                onClick={() => { fetchContractTemplates(); setSelectedTemplateId(''); setShowContractModal(true); }}
                data-testid="create-contract-btn"
              >
                <FileText className="w-4 h-4" />
                Sözleşme Oluştur
              </Button>
            )}
          </div>
          )} {/* end else (full stand editor) */}
        </DialogContent>
      </Dialog>
    ),

    /* Contract Creation Modal */
    ContractModal: () => (
      <>
      <Dialog open={showContractModal} onOpenChange={(o) => { setShowContractModal(o); if (!o) setSelectedTemplateId(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Sözleşme Oluştur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <p><span className="text-gray-500">Stand:</span> <strong>{editingStand?.stand_number || '-'}</strong></p>
              <p><span className="text-gray-500">Firma:</span> <strong>{editingStand?.assigned_company_name || '-'}</strong></p>
            </div>

            {contractTemplates.length === 0 ? (
              <div className="text-center py-4 text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Şablon bulunamadı.</p>
                <p className="text-xs mt-1">Ayarlar → Sözleşme Şablonları bölümünden şablon ekleyin.</p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Sözleşme Şablonu Seçin</Label>
                <div className="space-y-1.5" data-testid="template-select">
                  {contractTemplates.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(t.id)}
                      className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                        selectedTemplateId === t.id
                          ? 'border-teal-500 bg-teal-50 text-teal-800 font-medium'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                      data-testid={`template-option-${t.id}`}
                    >
                      <span className="flex items-center gap-2">
                        <FileText className="w-4 h-4 shrink-0 opacity-60" />
                        {t.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContractModal(false)}>İptal</Button>
            <Button
              onClick={handleCreateContract}
              disabled={!selectedTemplateId || creatingContract}
              data-testid="confirm-create-contract-btn"
            >
              {creatingContract ? 'Oluşturuluyor...' : 'Sözleşme Oluştur'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Warning Modal */}
      <Dialog open={showStatusWarning} onOpenChange={setShowStatusWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              Firma Atanmamış
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 py-2">
            Bu standı <strong>{warningStatusLabel}</strong> statüsüne çevirmek için lütfen önce <strong>Firma Atama</strong> alanından bir firma seçiniz.
          </p>
          <DialogFooter>
            <Button
              className="w-full"
              onClick={() => setShowStatusWarning(false)}
              data-testid="status-warning-ok-btn"
            >
              Anladım
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <SendEmailModal
        open={showMailModal}
        onClose={() => { setShowMailModal(false); setEditingStand(null); }}
        companyId={editingStand?.assigned_company_id}
        companyName={editingStand?.assigned_company_name}
      />
      </>
    )
  };
}

